import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Button, CircularProgress, Paper, InputAdornment, Stack, Box } from '@mui/material';
import { useTheme, keyframes } from '@mui/material/styles';
import { AttachMoney, WarningAmber, Business } from "@mui/icons-material";
import API from "../../../API";
import useAuth from "../../../Hooks/use-auth";
import { useMutation } from "react-query";
import useAlertDialog from "../../../Components/useAlertDialog";
import AlertDialog from "../../../Components/AlertDialog";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AgencyNegotiateDialog = ({
  open,
  handleClose,
  campaign,
  creatorId,
  currentRate,
  refreshCampaign,
  onSuccess,
}) => {
  const theme = useTheme();
  const { agencyToken } = useAuth();
  const [counterOffer, setCounterOffer] = useState('');
  const { dialogState, openDialog: openAlert, closeDialog } = useAlertDialog();

  const threshold = currentRate * 1.2;
  const counterOfferValue = parseFloat(counterOffer);
  const showWarning = counterOffer && 
                     !isNaN(counterOfferValue) && 
                     counterOfferValue > threshold;

  const { mutate: submitNegotiation, isLoading: isSubmitting } = useMutation(
    (offer) =>
      API.agency.campaignNegotiate(campaign.id, {
        counterOffer: parseFloat(offer),
        creator: creatorId,
      }),
    {
      onSuccess: () => {
        openAlert(
          "Counter Offer Submitted",
          "Your counter offer has been submitted on behalf of your creator. The brand will review your proposal.",
          () => {
            refreshCampaign();
            handleClose();
            closeDialog();
            if (onSuccess) onSuccess();
          },
          null,
          "Ok",
          null
        );
      },
      onError: (error) => {
        openAlert(
          "Error",
          `Error submitting counter offer: ${error.response?.data?.error || error.message}`,
          closeDialog,
          null,
          "Ok",
          null
        );
      }
    }
  );

  const platformFee = counterOffer
    ? (parseFloat(counterOffer) * 0.05).toFixed(2)
    : 0;
  const netPrice = counterOffer
    ? (parseFloat(counterOffer) - platformFee).toFixed(2)
    : 0;

  const handleSubmit = () => {
    if (!counterOffer || isNaN(counterOffer) || parseFloat(counterOffer) <= 0) {
      openAlert(
        "Invalid Offer",
        "Please enter a valid offer amount",
        closeDialog,
        null,
        "Ok",
        null
      );
      return;
    }
    submitNegotiation(counterOffer);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: theme.shadows[10],
            border: `1px solid ${theme.palette.divider}`
          }
        }}
      >
        <Paper elevation={0}>
          <DialogTitle
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.info.dark}, ${theme.palette.info.main})`,
              color: theme.palette.common.white,
              py: 3,
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Business fontSize="medium" />
              <Typography variant="h6" fontWeight="600">
                Counter Offer for {creatorId}
              </Typography>
            </Stack>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Box sx={{ 
                backgroundColor: theme.palette.grey[100],
                borderRadius: 2,
                p: 2,
                border: `1px solid ${theme.palette.divider}`
              }}>
                <Typography variant="subtitle1" color="text.secondary">
                  Current Offer
                </Typography>
                <Typography variant="h4" fontWeight="700" color="info.main">
                  ${currentRate?.toFixed(2)}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Agency Counter Offer"
                variant="filled"
                type="number"
                value={counterOffer}
                onChange={(e) =>
                  setCounterOffer(e.target.value.replace(/[^0-9.]/g, ""))
                }
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney color="action" />
                    </InputAdornment>
                  ),
                  sx: { fontSize: '1.1rem', fontWeight: 500 }
                }}
                helperText={showWarning && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.error.main,
                      animation: `${fadeIn} 0.3s ease-in`,
                      mt: 1
                    }}
                  >
                    <WarningAmber fontSize="small" sx={{ mr: 1 }} />
                    Counter offers above 20% of original rate may be rejected
                  </Box>
                )}
              />

              <Stack spacing={1.5}>
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: theme.palette.grey[50]
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Platform Fee (5%)
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    ${platformFee}
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.success.light,
                  border: `1px solid ${theme.palette.success.main}`
                }}>
                  <Typography variant="body1" fontWeight="600">
                    Creator Net Rate
                  </Typography>
                  <Typography variant="h6" fontWeight="700" color="success.dark">
                    ${netPrice}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" spacing={2} width="100%">
              <Button 
                onClick={handleClose} 
                variant="outlined" 
                color="inherit"
                fullWidth
                sx={{ 
                  borderRadius: 2,
                  py: 1,
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                fullWidth
                sx={{ 
                  borderRadius: 2,
                  py: 1,
                  boxShadow: theme.shadows[2],
                  '&:hover': { boxShadow: theme.shadows[4] }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.common.white }} />
                ) : "Submit Offer"}
              </Button>
            </Stack>
          </DialogActions>
        </Paper>
      </Dialog>
      <AlertDialog alertState={dialogState} />
    </>
  );
};

export default AgencyNegotiateDialog;